import { Link } from 'react-router-dom';
import { Text } from '../elements/text.element';

// Inline glyphs keep the footer dependency-free. Keyed by the ids useBrand()
// emits for each configured social profile.
const SOCIAL_ICON = {
  linkedin: <path d="M4.98 3.5A2.5 2.5 0 1 1 0 3.5a2.5 2.5 0 0 1 4.98 0ZM.24 8.2h4.5V24H.24V8.2Zm7.86 0h4.31v2.16h.06c.6-1.14 2.07-2.34 4.26-2.34 4.56 0 5.4 3 5.4 6.9V24h-4.5v-7.9c0-1.88-.03-4.3-2.62-4.3-2.62 0-3.02 2.05-3.02 4.17V24H8.1V8.2Z" />,
  twitter: <path d="M18.9 1.5h3.4l-7.4 8.5L23.6 22h-6.8l-5.3-7-6.1 7H2l7.9-9.1L1.4 1.5h7l4.8 6.4 5.7-6.4Zm-1.2 18.4h1.9L7.4 3.5H5.4l12.3 16.4Z" />,
  facebook: <path d="M24 12.07C24 5.4 18.63 0 12 0S0 5.4 0 12.07C0 18.1 4.39 23.1 10.13 24v-8.44H7.08v-3.49h3.05V9.41c0-3.02 1.79-4.69 4.53-4.69 1.31 0 2.68.24 2.68.24v2.96h-1.51c-1.49 0-1.96.93-1.96 1.89v2.26h3.33l-.53 3.49h-2.8V24C19.61 23.1 24 18.1 24 12.07Z" />,
  instagram: <path d="M12 2.16c3.2 0 3.58.01 4.85.07 3.25.15 4.77 1.69 4.92 4.92.06 1.27.07 1.65.07 4.85s-.01 3.58-.07 4.85c-.15 3.23-1.66 4.77-4.92 4.92-1.27.06-1.65.07-4.85.07s-3.58-.01-4.85-.07c-3.26-.15-4.77-1.7-4.92-4.92-.06-1.27-.07-1.65-.07-4.85s.01-3.58.07-4.85C2.38 3.92 3.89 2.38 7.15 2.23 8.42 2.17 8.8 2.16 12 2.16ZM12 0C8.74 0 8.33.01 7.05.07 2.7.27.28 2.69.08 7.05.01 8.33 0 8.74 0 12s.01 3.67.07 4.95c.2 4.36 2.62 6.78 6.98 6.98C8.33 23.99 8.74 24 12 24s3.67-.01 4.95-.07c4.35-.2 6.78-2.62 6.98-6.98.06-1.28.07-1.69.07-4.95s-.01-3.67-.07-4.95c-.2-4.35-2.62-6.78-6.98-6.98C15.67.01 15.26 0 12 0Zm0 5.84a6.16 6.16 0 1 0 0 12.32 6.16 6.16 0 0 0 0-12.32ZM12 16a4 4 0 1 1 0-8 4 4 0 0 1 0 8Zm6.4-11.85a1.44 1.44 0 1 0 0 2.88 1.44 1.44 0 0 0 0-2.88Z" />
};

// One link item → <Link> for in-app routes ({to}), <a> for external ({href}),
// or a plain <button> for an in-page action ({onClick}) like reopening the
// cookie banner — there is no route to navigate to for that one.
const FooterLink = ({ item, className }) =>
  item.onClick ? (
    <button type="button" aria-haspopup="dialog" onClick={item.onClick} className={className}>{item.label}</button>
  ) : item.to ? (
    <Link to={item.to} className={className}>{item.label}</Link>
  ) : (
    <a href={item.href} className={className}>{item.label}</a>
  );

const BrandMark = ({ brandName, brandLogo }) => (
  <Link to="/" className="inline-flex items-center" aria-label={brandName}>
    {brandLogo ? (
      <img src={brandLogo} alt={brandName} className="h-8 w-auto max-w-[140px] object-contain" />
    ) : (
      <Text variant="h1" className="text-base text-neutral">{brandName}</Text>
    )}
  </Link>
);

const SocialRow = ({ socialLinks = [] }) =>
  socialLinks.length > 0 ? (
    <div className="flex items-center gap-3">
      {socialLinks.map((item) => (
        <a
          key={item.id}
          href={item.href}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={item.label}
          title={item.label}
          className="text-gray-400 transition-colors hover:text-[var(--moksy-brand)]"
        >
          <svg viewBox="0 0 24 24" fill="currentColor" className="h-4 w-4" aria-hidden="true">
            {SOCIAL_ICON[item.id]}
          </svg>
        </a>
      ))}
    </div>
  ) : null;

const LINK_CLS = 'text-sm font-medium text-gray-500 transition-colors hover:text-neutral';
const LEGAL_CLS = 'text-xs font-medium text-gray-500 transition-colors hover:text-neutral';

// ── The canonical footer. One component, per-context variants:
//   'app'       lean single row (authenticated workspace) — DEFAULT, unchanged
//   'marketing' fat multi-column (public / landing / content pages)
//   'slim'      brand + copyright + legal only (auth, generating, report)
// Roles never change the footer — only the page context does. Link items and
// columns are supplied by core/Footer.jsx (dictionary-driven).
export const AppFooter = ({
  variant = 'app',
  brandName = '',
  brandLogo = '',
  copyright,
  docLink,
  docText,
  status,
  links = [],
  socialLinks = [],
  // marketing-only
  columns = [],
  legalLinks = [],
  tagline = '',
  cta = null,
  banner = null,       // { image, text, href } — optional badge strip on top
  companyInfo = '',    // registered-company text block (blank lines → paragraphs)
  legalLine = ''       // small print in the bottom bar (company no. / VAT)
}) => {
  // ── slim ──────────────────────────────────────────────────────────────────
  if (variant === 'slim') {
    return (
      <footer className="mt-auto border-t border-gray-200 bg-background px-6 py-5">
        <div className="mx-auto flex max-w-7xl flex-col items-start justify-between gap-3 md:flex-row md:items-center">
          <div className="flex items-center gap-3">
            <BrandMark brandName={brandName} brandLogo={brandLogo} />
            <Text variant="label" className="normal-case tracking-normal text-gray-500">{copyright}</Text>
          </div>
          <nav className="flex flex-wrap items-center gap-4">
            {(legalLinks.length ? legalLinks : links).map((link) => (
              <FooterLink key={link.to || link.href || link.label} item={link} className={LEGAL_CLS} />
            ))}
          </nav>
        </div>
      </footer>
    );
  }

  // ── marketing (fat) ─────────────────────────────────────────────────────────
  if (variant === 'marketing') {
    return (
      <footer className="mt-auto border-t border-gray-200 bg-background px-6 py-10">
        <div className="mx-auto max-w-7xl">
          {/* Optional banner/badge strip across the top (e.g. a partner logo). */}
          {banner && (banner.image || banner.text) ? (
            <div className="mb-8 border-b border-gray-100 pb-6">
              {(() => {
                const inner = (
                  <>
                    {banner.image ? (
                      <img src={banner.image} alt={banner.text || ''} className="h-10 w-auto max-w-[160px] object-contain" />
                    ) : null}
                    {banner.text ? (
                      <p className="text-sm font-medium text-neutral">{banner.text}</p>
                    ) : null}
                  </>
                );
                return banner.href ? (
                  <a href={banner.href} className="inline-flex items-center gap-3 hover:opacity-80">{inner}</a>
                ) : (
                  <div className="inline-flex items-center gap-3">{inner}</div>
                );
              })()}
            </div>
          ) : null}

          <div className="flex flex-col gap-10 md:flex-row md:justify-between">
            {/* Brand + tagline + CTA + company details */}
            <div className="max-w-xs">
              <BrandMark brandName={brandName} brandLogo={brandLogo} />
              {tagline ? (
                <p className="mt-3 text-sm leading-relaxed text-gray-500">{tagline}</p>
              ) : null}
              {cta ? (
                <FooterLink
                  item={cta}
                  className="mt-4 inline-flex items-center rounded-full bg-brand px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-brand-dark"
                />
              ) : null}
              {companyInfo ? (
                <div className="mt-5 space-y-2 text-xs leading-relaxed text-gray-500">
                  {companyInfo.split(/\n{2,}/).map((para, i) => (
                    <p key={i}>{para.split('\n').map((line, j) => (
                      <span key={j}>{line}{j < para.split('\n').length - 1 ? <br /> : null}</span>
                    ))}</p>
                  ))}
                </div>
              ) : null}
            </div>

            {/* Link columns — flex so 2 or 4 columns both pack cleanly. */}
            {columns.length > 0 ? (
              <div className="flex flex-wrap gap-10 sm:gap-16">
                {columns.map((col) => (
                  <div key={col.title} className="min-w-[120px]">
                    <div className="text-sm font-semibold text-neutral">{col.title}</div>
                    <ul className="mt-3 flex flex-col gap-2">
                      {(col.links || []).map((link) => (
                        <li key={link.to || link.href || link.label}>
                          <FooterLink item={link} className={LINK_CLS} />
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            ) : null}
          </div>

          {/* Legal strip: copyright + small print + legal links · status · social */}
          <div className="mt-10 flex flex-col gap-3 border-t border-gray-200 pt-5 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
              <Text variant="label" className="normal-case tracking-normal text-gray-400">{copyright}</Text>
              {legalLine ? (
                <span className="text-xs text-gray-400">{legalLine}</span>
              ) : null}
              {legalLinks.map((link) => (
                <FooterLink key={link.to || link.href || link.label} item={link} className={LEGAL_CLS} />
              ))}
            </div>
            <div className="flex flex-wrap items-center gap-4">
              {status}
              <SocialRow socialLinks={socialLinks} />
            </div>
          </div>
        </div>
      </footer>
    );
  }

  // ── app (lean) — default, unchanged behaviour ───────────────────────────────
  return (
    <footer className="mt-auto border-t border-gray-200 bg-background px-6 py-6">
      <div className="mx-auto flex max-w-7xl flex-col items-start justify-between gap-4 md:flex-row md:items-center">
        <div className="flex flex-wrap items-center gap-3">
          <BrandMark brandName={brandName} brandLogo={brandLogo} />
          <Text variant="label" className="normal-case tracking-normal text-gray-500">{copyright}</Text>
        </div>

        {status}

        <div className="flex flex-wrap items-center gap-4">
          <nav className="flex flex-wrap items-center gap-4">
            {Array.isArray(links) && links.length > 0 ? (
              links.map((link) => (
                <FooterLink key={link.to || link.href || link.label} item={link} className={LINK_CLS} />
              ))
            ) : docLink ? (
              <a href={docLink} className={LINK_CLS}>{docText}</a>
            ) : null}
          </nav>
          <SocialRow socialLinks={socialLinks} />
        </div>
      </div>
    </footer>
  );
};
